import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { isBoard, isInvestor, readClaims, wasBoard, wasInvestor, type ExcellenceClaims } from '@/lib/session'
import { INVESTOR_SURFACE_KEY } from '@/modules/data-room/investor/surface'
import { rememberedBoard } from '@/modules/board/member/surface'

/**
 * The `user_type` switch (D44) — one switch, at the shell, before anything
 * else renders.
 *
 *   staff           → `App` (the internal shell, unchanged)
 *   investor        → the data room shell, and nothing else
 *   board           → the board member's shell, and nothing else (D77)
 *   partner / none  → `App`, which refuses them exactly as before
 *
 * It is the THIRD layer and never the first: an investor session runs as
 * `investor_user`, which holds no grant outside `investor_api` and `claims`, so
 * rendering the wrong shell would draw screens whose every query answers
 * 42501. What this layer adds is that an investor never downloads the internal
 * shell either: both surfaces are `lazy()` chunks, so the internal registry —
 * twenty modules, the CRM, HR, Finance — is never fetched by a browser that
 * holds an investor token. Before this file the whole app was one bundle and
 * `App` refused every non-staff session after downloading all of it.
 *
 * Partner handling is deliberately untouched: a partner still reaches `App`,
 * which still renders `NotProvisioned`. The portal is its own piece of work.
 */
const StaffApp = lazy(() => import('./App'))
const InvestorApp = lazy(() => import('@/modules/data-room/investor/InvestorApp'))
const InvestorGate = lazy(() => import('@/modules/data-room/investor/InvestorGate'))
// The fourth audience (D77, BOARD.md §2.8): its own chunks, like the investor's.
const BoardApp = lazy(() => import('@/modules/board/member/BoardApp'))
const BoardGate = lazy(() => import('@/modules/board/member/BoardGate'))
// A partner signing a contract (D74.4): no account, the link is the credential.
const SignPage = lazy(() => import('@/modules/partners/sign/SignPage'))
// Someone outside signing a board-level agreement (20261030090000): same rule.
const BoardSignPage = lazy(() => import('@/modules/board/sign/BoardSignPage'))

function rememberedInvestor(): boolean {
  try {
    return localStorage.getItem(INVESTOR_SURFACE_KEY) === 'investor'
  } catch {
    return false
  }
}

function Loading() {
  return (
    <div className="grid h-full place-items-center text-sm text-muted-foreground">
      Loading…
    </div>
  )
}

export default function Root() {
  const { pathname } = useLocation()
  const [session, setSession] = useState<Session | null>(null)
  const [claims, setClaims] = useState<ExcellenceClaims | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setClaims(readClaims(data.session))
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setClaims(readClaims(next))
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  if (loading) return <Loading />

  let surface: ReactNode
  if (pathname.startsWith('/sign/board/')) {
    // Before `/sign/`: the board's link, not a partner's.
    surface = <BoardSignPage />
  } else if (pathname.startsWith('/sign/')) {
    // Whoever is or is not signed in: the signer has no account here.
    surface = <SignPage />
  } else if (pathname === '/data-room/sign-in') {
    // The invite link lands here with a token_hash, whoever is signed in.
    surface = <InvestorGate mode="sign-in" claims={claims} />
  } else if (pathname === '/board/sign-in') {
    // The board's invite and re-link mails land here, whoever is signed in.
    surface = <BoardGate mode="sign-in" claims={claims} />
  } else if (session && isInvestor(claims)) {
    surface = <InvestorApp claims={claims!} />
  } else if (session && wasInvestor(claims)) {
    // The window closed and the hook now answers `anon`. Say so, in English,
    // rather than "your account is not provisioned".
    surface = <InvestorGate mode="ended" claims={claims} />
  } else if (session && isBoard(claims)) {
    surface = <BoardApp claims={claims!} />
  } else if (session && wasBoard(claims)) {
    // The last seat ended and the hook now answers `anon`.
    surface = <BoardGate mode="ended" claims={claims} />
  } else if (
    !session &&
    (pathname === '/data-room/signed-out' || (pathname.startsWith('/data-room') && rememberedInvestor()))
  ) {
    surface = <InvestorGate mode="signed-out" claims={null} />
  } else if (
    !session &&
    (pathname === '/board/signed-out' ||
      ((pathname === '/' || pathname.startsWith('/board')) && rememberedBoard()))
  ) {
    // `/` too: a member's bookmark of the home page would otherwise land on
    // the Microsoft screen (§2.7).
    surface = <BoardGate mode="signed-out" claims={null} />
  } else {
    surface = <StaffApp />
  }

  return <Suspense fallback={<Loading />}>{surface}</Suspense>
}
